"use client";

import { Code, Database, Server } from "lucide-react";
import { motion } from "framer-motion";
import Image from "next/image";
import PageTemplate from "@/components/PageTemplate";
import { Products } from "@/types/productsTypes";
import FloatingAction from "src/components/FloatingAction";

export default function MarketplaceSection({ section }: { section: Products['atlaside']['marketplaceSection'] }) {

    return (
        <section className="section-primary mb-52">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        viewport={{ once: true, margin: "-100px" }}
                        className="inline-block"
                    >
                        <p className="w-fit mx-auto badge-size section-badge mb-1">{section.badge}</p>
                    </motion.div>
                    
                    <motion.h2 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                        viewport={{ once: true, margin: "-100px" }}
                        className="title-size font-bold title-bg-secondary mb-6"
                    >
                        {section.title}
                    </motion.h2>
                    
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                        viewport={{ once: true, margin: "-100px" }}
                        className="text-size text-gray-600 max-w-3xl mx-auto leading-relaxed"
                    >
                        {section.description}
                    </motion.p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                        viewport={{ once: true, margin: "-50px" }}
                        className="relative max-w-5xl mx-auto xl:col-span-2 h-auto xl:h-[500px]"
                    >
                        <PageTemplate title="Marketplace Interface" description="Browse and deploy templates" icon={<Code className="size-6 text-white" />}>                
                            {/* Template grid mockup */}
                            <div className="relative">
                                <Image 
                                    src={section.mainImage}
                                    alt="Template 1" 
                                    width={1000} 
                                    height={1000} 
                                    className="w-full h-full object-cover rounded-2xl"
                                />
                                
                                {/* Overlay gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                            </div>
                            
                            <FloatingAction position="-bottom-4 -right-4" text={section.floatingAction.text} icon={section.floatingAction.icon} />
                        </PageTemplate>
                    </motion.div>

                    <motion.div 
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ staggerChildren: 0.2, delayChildren: 0.4 }}
                        className="grid grid-cols-1 gap-6 mt-8 xl:mt-0"
                    >
                        {section.cards.map((card, idx) => (
                            <motion.div
                                key={card.title}
                                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                                whileHover={{ scale: 1.02, y: -5 }}
                                className={
                                    idx === 0
                                        ? "group text-left border border-gray-200 rounded-xl p-6 bg-gradient-to-br from-white/90 to-blue-50/50 backdrop-blur-sm hover:shadow-xl transition-all duration-300 hover:border-blue-200"
                                        : idx === 1
                                        ? "group text-left border border-gray-200 rounded-xl p-6 bg-gradient-to-br from-white/90 to-purple-50/50 backdrop-blur-sm hover:shadow-xl transition-all duration-300 hover:border-purple-200"
                                        : "group text-left border border-gray-200 rounded-xl p-6 bg-gradient-to-br from-white/90 to-green-50/50 backdrop-blur-sm hover:shadow-xl transition-all duration-300 hover:border-green-200"
                                }
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">{card.title}</h3>
                                        <p className="text-gray-600 text-base leading-relaxed">{card.description}</p>
                                    </div>
                                    <motion.div
                                        className={
                                            idx === 0
                                                ? "size-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-3 shadow-lg"
                                                : idx === 1
                                                ? "size-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-3 shadow-lg"
                                                : "size-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-3 shadow-lg"
                                        }
                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {card.icon === 'code' && <Code className="size-6 text-white" />}
                                        {card.icon === 'database' && <Database className="size-6 text-white" />}
                                        {card.icon === 'server' && <Server className="size-6 text-white" />}
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}; 